import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { InfoBoxComponent } from '../utility/info-box/info-box.component';
import { Router } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FolderSquareComponent } from '../utility/folder-square/folder-square.component';
import { IFolderSquare } from '../../interfaces/folder-square.interface';
import { HttpService } from '../../services/http.service';
import { StorageService } from '../../services/storage.service';
import { UserAccountDTO } from '../../classes/user-account.dto';
import { MedicalRecordMenuInfo } from '../../interfaces/medical-history.interface';
import { LoadDocumentsModalComponent } from '../modals/load-documents-modal/load-documents-modal.component';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { DocumentCardComponent } from '../document-card/document-card.component';
import format from 'date-fns/format';
import { IDocumentCard, IDocumentEmitter } from '../../interfaces/document-card.interface';
import { ToastrService } from 'ngx-toastr';
import { ModalWithTwoButtonsComponent } from '../modals/modal-with-two-buttons/modal-with-two-buttons.component';
import { AppointmentsTableComponent } from '../appointments-table/appointments-table.component';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'app-reports-page',
  standalone: true,
  imports: [TranslateModule, NzDividerModule, CommonModule, InfoBoxComponent, NzIconModule, FolderSquareComponent, DocumentCardComponent, AppointmentsTableComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './reports-page.component.html',
  styleUrl: './reports-page.component.scss',
})
export class ReportsPageComponent implements OnInit {
  folderSquareContent: IFolderSquare[] = [];
  medicalRecord?: MedicalRecordMenuInfo;
  recentCardData?: Array<IDocumentCard> = [];
  columns: any;
  rows: any;
  appointmentsText: any;
  innerSelectData: any[] = [];

  constructor(
    private router: Router,
    private http: HttpService,
    private storage: StorageService,
    private modalService: NzModalService,
    private toast: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.getMedicalRecord();
    this.getDocuments();
    this.getColumns();
    this.translate.onLangChange.subscribe(() => {
      this.getColumns();
    });
  }

  protected back(): void {
    this.router.navigate(['/health-folder']);
  }

  private getMedicalRecord(): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;
    this.http.getMedicalRecordMenuInfo(individualId).subscribe((medicalRecord) => {
      this.medicalRecord = medicalRecord;
      this.generateFolderSquareContent();
    });
  }

  private getRows(documentsData: any): void {
    const rows: any[] = [];
    if (!documentsData?.lista) {
      this.rows = [];
      return;
    }
    documentsData.lista.forEach((doc: any) => {
      rows.push({
        consultDate: format(new Date(doc.documentDate), 'dd/MM/yyyy HH:mm'),
        name: doc.title,
        specialistNotTranslated: doc.category,
        consultType: doc.consultType,
        chargedBy: doc.creationUser ? doc.creationUser : 'Medico Generico',
        dropdown: true,
        downloadUrl: doc.downloadUrl,
        idDoc: doc.id,
        individualId: doc.individualId,
        docName: doc.documentName,
      });
    });
    this.rows = rows;
  }

  private getDocuments(): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;
    this.http.getDocuments(individualId).subscribe((documentsData) => {
      this.createCardData(documentsData);
      this.getRows(documentsData);
    });
  }

  private createCardData(documentsData: any): void {
    this.recentCardData = [];
    documentsData.lista.forEach((document: any, index: number) => {
      if (index < 3) {
        this.recentCardData?.push({
          id: document.id,
          icon: document.type.startsWith('Referto') ? 'file-icon.svg' : 'file-blue.svg',
          title: document.documentName,
          date: format(new Date(document.documentDate), 'dd MMM yyyy'),
          type: document.type,
          additional_text: 'reportsPage.document_type',
        });
      }
    });
  }

  protected actionCard(event: IDocumentEmitter): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;
    if (event.eventType === 'delete') {
      const textsModal = this.translate.instant('deleteDocumentModal');
      const activeModal = this.modalService.create({
        nzContent: ModalWithTwoButtonsComponent,
        nzClosable: true,
        nzFooter: null,
        nzData: {
          dataModal: {
            colorActionButton: 'red',
            texts: {
              ...textsModal,
              first_button_pointer: 'delete',
            },
            actionButton: () => {
              this.http.deleteDocument(individualId, event.idDoc).subscribe({
                next: (_) => {
                  activeModal.close();
                  this.toast.success(this.translate.instant('reportsPage.deleted_successfully'));
                  this.getDocuments();
                  this.getMedicalRecord();
                },
              });
            },
          },
        },
      });
    } else if (event.eventType === 'download') {
      this.http.downloadSingleDocument(individualId, event.docName, true).subscribe((urlObj: { docUrl: string }) => {
        const link = document.createElement('a');
        link.href = urlObj.docUrl;
        link.download = event.docName;
        link.click();
      });
    } else if (event.eventType === 'view') {
      /* TODO: Da definire */
      this.http.downloadSingleDocument(individualId, event.docName, false).subscribe();
    }
  }

  private generateFolderSquareContent(): void {
    if (this.medicalRecord) {
      this.folderSquareContent = [];
      this.folderSquareContent?.push(
        {
          id: 'reports',
          image: 'file-icon.svg',
          title: 'reportsPage.folderSquare.reports',
          subtitle: 'reportsPage.folderSquare.files',
          value: this.medicalRecord.documentCount?.toString(),
          style: 'white',
        },
        {
          id: 'prescriptions',
          image: 'file-blue.svg',
          title: 'reportsPage.folderSquare.prescriptions',
          subtitle: 'reportsPage.folderSquare.files',
          value: this.medicalRecord.prescriptionCount?.toString(),
          style: 'white',
        },
        {
          id: 'load_reports',
          image: 'plus-circle.svg',
          title: 'reportsPage.folderSquare.load_reports',
          style: 'blue',
        }
      );
    }
  }

  protected handleClickFolders(id: string): void {
    if (id === 'load_reports') {
      const textsModalWithTwoButtons = this.translate.instant('loadDocumentModal.firstStep');
      const modalTwoButtonsRef: NzModalRef<ModalWithTwoButtonsComponent> = this.modalService.create({
        nzContent: ModalWithTwoButtonsComponent,
        nzClosable: true,
        nzFooter: null,
        nzData: {
          dataModal: {
            texts: {
              ...textsModalWithTwoButtons,
              first_button_pointer: 'proceed',
            },
            actionButton: () => {
              modalTwoButtonsRef.close('next');
            },
          },
        },
      });

      modalTwoButtonsRef.afterClose.subscribe((event) => {
        if (event === 'next') {
          const modalLoadDocumentsRef: NzModalRef<LoadDocumentsModalComponent> = this.modalService.create({
            nzContent: LoadDocumentsModalComponent,
            nzClosable: true,
            nzFooter: null,
            nzData: {
              dataModal: { isFromMedicalConsultations: false },
            },
          });

          modalLoadDocumentsRef.afterClose.subscribe((result: { event: string; documentData: IDocumentCard }) => {
            if (result.event === 'success') {
              this.getDocuments();
              this.getMedicalRecord();
            }
          });
        }
      });
    }
  }

  private getColumns() {
    this.translate.get('reportsPage').subscribe((texts: any) => {
      this.columns = texts.columns;
    });
    this.translate.get('reportsPage').subscribe((texts: any) => {
      this.appointmentsText = texts.appointmentsText;
    });
    this.getInnerSelectData();
  }

  downloadAll() {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;
    this.http.downloadAllDocuments(individualId).subscribe((fileBlob: any) => {
      const anchor = document.createElement('a');
      anchor.href = fileBlob.url;
      anchor.click();
    });
  }

  shareAll() {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;
    const shareUrl = `https://qaapi.myclinic.europassistance.it/api/medicalrecord/medicalpassport/${individualId}/ita/GEN/false`;
    const mailToLink = `mailto:?subject=La mia storia medica&body=${encodeURIComponent(shareUrl)}`;
    this.router.navigate([mailToLink]);
  }

  private getInnerSelectData() {
    this.translate.get('reportsPage').subscribe((texts) => {
      texts?.innerSelectData?.forEach((text: any) => {
        const innerSelectData = {
          placeholder: text.placeholder,
          label: text.label,
          texts: text.texts,
          elements: text.elements,
          control: new FormControl(null, [Validators.required]),
        };
        this.innerSelectData.push(innerSelectData);
      });
    });
  }
}
